/**
 * EditTripPage - Edit an existing trip's details
 * Route: /trips/:id/edit
 * Design: tripways.pen node k2rjKJ (desktop) / NyyNq (mobile)
 *
 * Cover photo upload is styled per the design but disabled — this repo has
 * no file-upload infrastructure (no MultipartFile/S3/upload endpoint)
 * anywhere yet, so wiring it up is out of scope here.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, Trash2 } from 'lucide-react';
import { apiGet } from '../services/apiClient';
import { useTrips } from '../contexts/TripContext';
import { useToast } from '../contexts/ToastContext';
import { formatDateRange, parseDate } from '../utils/dates';
import DesktopSidebar from '../components/common/DesktopSidebar';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingScreen from '../components/common/LoadingScreen';
import type { TripDetailResponse } from '../types/trip';
import type { MemberRole } from '../types/member';

export default function EditTripPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { updateTrip, deleteTrip } = useTrips();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [myRole, setMyRole] = useState<MemberRole>('EDITOR');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [destination, setDestination] = useState('');
  const [travelerCount, setTravelerCount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!id) return;
    apiGet<TripDetailResponse>(`/trip/${id}`).then((response) => {
      if (response.exitCode === 1 && response.data) {
        const trip = response.data;
        setName(trip.name);
        setDescription(trip.description ?? '');
        setDestination(trip.destination ?? '');
        setTravelerCount(trip.travelerCount != null ? String(trip.travelerCount) : '');
        setStartDate(trip.startDate);
        setEndDate(trip.endDate);
        setMyRole(trip.myRole);
      } else {
        showToast('error', '無法載入旅程資料');
        navigate('/trips');
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return null;

  const handleCancel = () => navigate(-1);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = '請輸入旅程名稱';
    if (!startDate) newErrors.startDate = '請選擇開始日期';
    if (!endDate) newErrors.endDate = '請選擇結束日期';
    if (startDate && endDate && parseDate(endDate) < parseDate(startDate)) {
      newErrors.endDate = '結束日期不能早於開始日期';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await updateTrip(id, {
        name: name.trim(),
        description: description.trim() || undefined,
        destination: destination.trim() || undefined,
        travelerCount: travelerCount ? Number(travelerCount) : undefined,
        startDate: parseDate(startDate),
        endDate: parseDate(endDate),
      });
      showToast('success', '已儲存旅程變更');
      navigate(`/trips/${id}`);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : '儲存失敗，請稍後再試');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteTrip(id);
      showToast('success', '已刪除旅程');
      navigate('/trips');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : '刪除旅程失敗');
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const heroDateRange =
    startDate && endDate ? formatDateRange(parseDate(startDate), parseDate(endDate)) : '';

  return (
    <div className="flex min-h-screen bg-[#f5f8f8]">
      <DesktopSidebar onNavigate={navigate} tripId={id} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* ─── Mobile Header ─── */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-100">
          <div className="flex items-center justify-between px-5 py-4">
            <button onClick={handleCancel} className="flex items-center justify-center w-11 h-11 -ml-2.5">
              <span className="text-[15px] text-slate-400">取消</span>
            </button>
            <h1 className="text-slate-900 text-[17px] font-semibold">編輯旅程</h1>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center justify-center w-11 h-11 -mr-2.5 disabled:opacity-50"
            >
              <span className="text-[15px] font-semibold text-primary">儲存</span>
            </button>
          </div>
        </header>

        {/* ─── Desktop Top Bar ─── */}
        <header className="hidden lg:flex items-center justify-between h-[72px] bg-white border-b border-slate-100 px-10">
          <div className="flex items-center gap-4">
            <button
              onClick={handleCancel}
              className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-900 hover:bg-slate-50 transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-slate-900 text-[22px] font-bold">編輯旅程</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="flex items-center h-10 px-5 rounded-lg border border-slate-200 text-sm font-medium text-slate-900 hover:bg-slate-50 transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center h-10 px-5 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50"
            >
              儲存變更
            </button>
          </div>
        </header>

        {/* ─── Main ─── */}
        <main className="flex-1">
          {/* Mobile */}
          <div className="lg:hidden">
            <div className="relative h-[180px] overflow-hidden">
              <img
                alt="山景與夕陽的旅行風景"
                className="h-full w-full object-cover"
                src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=400&fit=crop"
              />
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                <button
                  disabled
                  title="封面圖片上傳功能尚未推出"
                  className="flex items-center justify-center w-12 h-12 rounded-full bg-black/20 text-white cursor-not-allowed"
                >
                  <Camera size={24} />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-5 px-5 py-5">
              <FormField label="旅程名稱" error={errors.name}>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-[15px] focus:outline-none focus:border-primary"
                />
              </FormField>

              <FormField label="目的地">
                <input
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="例如：東京"
                  className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-[15px] placeholder:text-slate-300 focus:outline-none focus:border-primary"
                />
              </FormField>

              <div className="flex gap-3">
                <FormField label="開始日期" error={errors.startDate} className="flex-1">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-[15px] focus:outline-none focus:border-primary"
                  />
                </FormField>
                <FormField label="結束日期" error={errors.endDate} className="flex-1">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-[15px] focus:outline-none focus:border-primary"
                  />
                </FormField>
              </div>

              <FormField label="旅伴人數">
                <input
                  type="number"
                  min={1}
                  value={travelerCount}
                  onChange={(e) => setTravelerCount(e.target.value)}
                  placeholder="例如：4"
                  className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-[15px] placeholder:text-slate-300 focus:outline-none focus:border-primary"
                />
              </FormField>

              <FormField label="旅程描述（選填）">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full rounded-[10px] border border-slate-200 px-3.5 py-3 text-[15px] resize-none focus:outline-none focus:border-primary"
                />
              </FormField>

              {myRole === 'OWNER' && (
                <div className="flex flex-col gap-3 pt-1">
                  <div className="flex flex-col gap-2">
                    <div className="h-px bg-slate-200" />
                    <span className="text-[11px] font-semibold text-slate-400">危險區域</span>
                  </div>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center justify-center gap-1.5 w-full h-11 rounded-[10px] border border-red-500 text-red-500 text-sm font-medium"
                  >
                    <Trash2 size={16} />
                    刪除旅程
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Desktop */}
          <div className="hidden lg:flex items-center justify-center p-10">
            <div className="flex w-[960px] rounded-2xl bg-white shadow-lg overflow-hidden">
              {/* Left hero panel */}
              <div className="relative w-[400px] shrink-0 flex flex-col justify-end p-8 overflow-hidden">
                <img
                  alt="山景與夕陽的旅行風景"
                  className="absolute inset-0 h-full w-full object-cover"
                  src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=900&fit=crop"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                <div className="relative flex flex-col gap-2 text-white">
                  <h2 className="text-2xl font-bold">{name || '旅程名稱'}</h2>
                  {heroDateRange && <p className="text-sm text-white/80">{heroDateRange}</p>}
                </div>
              </div>

              {/* Right form panel */}
              <div className="flex-1 flex flex-col gap-6 p-10">
                <div>
                  <h2 className="text-slate-900 text-xl font-bold">旅程資訊</h2>
                  <p className="text-slate-400 text-[13px] mt-1">修改旅程的基本設定</p>
                </div>

                <FormField label="旅程名稱" error={errors.name}>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-sm focus:outline-none focus:border-primary"
                  />
                </FormField>

                <FormField label="目的地">
                  <input
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    placeholder="例如：東京"
                    className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-sm placeholder:text-slate-300 focus:outline-none focus:border-primary"
                  />
                </FormField>

                <div className="flex gap-4">
                  <FormField label="開始日期" error={errors.startDate} className="flex-1">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-sm focus:outline-none focus:border-primary"
                    />
                  </FormField>
                  <FormField label="結束日期" error={errors.endDate} className="flex-1">
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-sm focus:outline-none focus:border-primary"
                    />
                  </FormField>
                </div>

                <FormField label="旅伴人數">
                  <input
                    type="number"
                    min={1}
                    value={travelerCount}
                    onChange={(e) => setTravelerCount(e.target.value)}
                    placeholder="例如：4"
                    className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-sm placeholder:text-slate-300 focus:outline-none focus:border-primary"
                  />
                </FormField>

                <FormField label="旅程描述（選填）">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full rounded-[10px] border border-slate-200 px-3.5 py-3 text-sm resize-none focus:outline-none focus:border-primary"
                  />
                </FormField>

                <div className="flex items-center justify-between">
                  <span className="text-slate-900 text-[13px] font-semibold">封面照片</span>
                  <button
                    disabled
                    title="封面圖片上傳功能尚未推出"
                    className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-slate-200 text-slate-400 text-[13px] font-medium cursor-not-allowed"
                  >
                    <Camera size={14} />
                    更換封面
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="刪除旅程"
        message={`確定要刪除「${name}」嗎？此操作將永久刪除該旅程及其所有行程、成員與投票，且無法復原。`}
        confirmText="刪除"
      />
    </div>
  );
}

function FormField({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-2 ${className ?? ''}`}>
      <label className="text-[13px] font-semibold text-slate-900">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

